import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, map, tap, filter, take, catchError } from 'rxjs/operators';
import { AppLoadingService } from './app-loading.service';
import { environment } from '../../../environments/environment';
import { getEnvVar } from '../../../environments/env-loader';

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
}

export interface LoginRequest {
  email: string; // Can be email or username
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: User;
  token: string;
}

export interface UserPermissions {
  avatar: boolean;
  media: boolean;
  documents: boolean;
  chat: boolean;
  messages: boolean;
  diary: boolean;
  timeCapsule: boolean;
}

export interface BackendLoginResponse {
  success: boolean;
  message: string;
  data: {
    access_token: string;
    refresh_token: string; // Always present - stored in localStorage
    user: {
      id: number;
      email: string;
      name: string;
      role: string;
    };
    permissions?: UserPermissions; // Only for sub-users
  };
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  data: {
    access_token: string;
    refresh_token: string; // New refresh token after rotation
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private authInitializedSubject = new BehaviorSubject<boolean>(false);

  public currentUser$ = this.currentUserSubject.asObservable();
  public token$ = this.tokenSubject.asObservable();
  public authInitialized$ = this.authInitializedSubject.asObservable();

  // Backend API URL
  private readonly API_URL = getEnvVar('REACT_APP_API_URL', environment.apiUrl);

  // Storage keys
  private readonly USER_KEY = 'current_user';
  private readonly REFRESH_TOKEN_KEY = 'app_refresh_token';
  private readonly PERMISSIONS_KEY = 'user_permissions';
  
  private permissionsSubject = new BehaviorSubject<UserPermissions | null>(null);
  public permissions$ = this.permissionsSubject.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Auth will be initialized via APP_INITIALIZER
  }

  /**
   * Simple XOR encryption for localStorage tokens
   * Note: This is NOT strong encryption, just obfuscation to prevent casual inspection
   * For true security, rely on HTTPS, short-lived tokens, and backend validation
   */
  private encryptToken(token: string): string {
    const key = 'abgl-k3y'; // Simple key for XOR
    let encrypted = '';
    for (let i = 0; i < token.length; i++) {
      encrypted += String.fromCharCode(token.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(encrypted); // Base64 encode
  }

  private decryptToken(encrypted: string): string {
    try {
      const decoded = atob(encrypted); // Base64 decode
      const key = 'abgl-k3y';
      let decrypted = '';
      for (let i = 0; i < decoded.length; i++) {
        decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return decrypted;
    } catch (error) {
      return '';
    }
  }

  /**
   * Store refresh token securely in localStorage
   */
  private storeRefreshToken(token: string): void {
    if (isPlatformBrowser(this.platformId)) {
      const encrypted = this.encryptToken(token);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, encrypted);
    }
  }

  /**
   * Retrieve refresh token from localStorage
   */
  private getRefreshToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const encrypted = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      if (encrypted) {
        return this.decryptToken(encrypted);
      }
    }
    return null;
  }

  /**
   * Remove refresh token from localStorage
   */
  private removeRefreshToken(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }
  }

  /**
   * Login API - authenticates with backend server
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<BackendLoginResponse>(`${this.API_URL}/auth/login`, credentials).pipe(
      map(backendResponse => {
        if (!backendResponse.success) {
          throw new Error(backendResponse.message || 'Login failed');
        }

        // Transform backend response to frontend format
        const user: User = {
          id: backendResponse.data.user.id,
          email: backendResponse.data.user.email,
          name: backendResponse.data.user.name,
          role: backendResponse.data.user.role || 'SUB_USER' // Use role from backend, default to SUB_USER
        };

        const response: LoginResponse = {
          user,
          token: backendResponse.data.access_token
        };

        // Store refresh token securely in localStorage (encrypted)
        if (backendResponse.data.refresh_token) {
          this.storeRefreshToken(backendResponse.data.refresh_token);
        } else {
        }

        // Store permissions for sub-users
        if (backendResponse.data.permissions) {
          this.storePermissions(backendResponse.data.permissions);
        }

        return response;
      }),
      tap(response => {
        // Store authentication data (access token in memory only, refresh token in HttpOnly cookie)
        this.setAuthData(response.user, response.token);
        // Ensure auth is marked as initialized after login
        this.authInitializedSubject.next(true);
      })
    );
  }
    subUserlogin(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<BackendLoginResponse>(`${this.API_URL}/auth/login-sub-user`, credentials).pipe(
      map(backendResponse => {
        if (!backendResponse.success) {
          throw new Error(backendResponse.message || 'Login failed');
        }

        // Transform backend response to frontend format
        const user: User = {
          id: backendResponse.data.user.id,
          email: backendResponse.data.user.email,
          name: backendResponse.data.user.name,
          role: backendResponse.data.user.role || 'SUB_USER' // Use role from backend, default to SUB_USER
        };

        const response: LoginResponse = {
          user,
          token: backendResponse.data.access_token
        };

        // Store refresh token securely in localStorage (encrypted)
        if (backendResponse.data.refresh_token) {
          this.storeRefreshToken(backendResponse.data.refresh_token);
        } else {
        }

        // Store permissions for sub-users
        if (backendResponse.data.permissions) {
          this.storePermissions(backendResponse.data.permissions);
        }

        return response;
      }),
      tap(response => {
        // Store authentication data (access token in memory only, refresh token in HttpOnly cookie)
        this.setAuthData(response.user, response.token);
        // Ensure auth is marked as initialized after login
        this.authInitializedSubject.next(true);
      })
    );
  }

  /**
   * Logout user and clear stored data
   */
  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    
    // Call backend logout endpoint with refresh token in body
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/auth/logout`, {
      refresh_token: refreshToken
    }).pipe(
      tap(response => {
        this.clearAuthData();
      }),
      map(() => void 0),
      // Always clear data even if backend call fails
      catchError((error: any) => {
        this.clearAuthData();
        return of(void 0);
      })
    );
  }

  /**
   * Logout from all devices
   */
  logoutAllDevices(): Observable<void> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/auth/logout-all`, {}).pipe(
      tap(response => {
        this.clearAuthData();
      }),
      map(() => void 0),
      catchError((error: any) => {
        this.clearAuthData();
        return of(void 0);
      })
    );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    
    if (!token) {
      return false;
    }
    
    const isExpired = this.isTokenExpired(token);
    
    return !!token && !isExpired;
  }

  /**
   * Check if auth is initialized
   */
  isAuthInitialized(): boolean {
    return this.authInitializedSubject.value;
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get current token (from memory only, not localStorage)
   */
  getToken(): string | null {
    return this.tokenSubject.value;
  }

  /**
   * Refresh token using backend API
   * Sends refresh token from localStorage in request body
   */
  refreshToken(): Observable<string> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.clearAuthData();
      return throwError(() => new Error('No refresh token available'));
    }


    return this.http.post<RefreshTokenResponse>(`${this.API_URL}/auth/refresh`, {
      refresh_token: refreshToken
    }).pipe(
      map(response => {
        if (!response.success) {
          throw new Error('Token refresh failed');
        }

        const newAccessToken = response.data.access_token;
        const newRefreshToken = response.data.refresh_token;

        // Store new refresh token (token rotation)
        this.storeRefreshToken(newRefreshToken);

        // Store new access token in memory only
        this.tokenSubject.next(newAccessToken);

        return newAccessToken;
      }),
      catchError((error: any) => {
        // If refresh fails, clear auth data
        this.clearAuthData();
        return throwError(() => error);
      })
    );
  }

  /**
   * Request password reset OTP (supports email or phone)
   */
  sendForgotPasswordOtp(payload: { email?: string; phone?: string }): Observable<{ message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/auth/forgot-password-otp`, payload).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Password reset failed');
        }
        return { message: response.message };
      })
    );
  }

  /**
   * Request password reset OTP via email or phone
   */
  requestPasswordResetOtp(payload: { email?: string; phone?: string }): Observable<{ message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/auth/forgot-password-otp`, payload).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Password reset request failed');
        }
        return { message: response.message };
      })
    );
  }

  /**
   * Verify OTP (email or phone)
   */
  verifyOtp(payload: { email?: string; phone?: string; otp: string }): Observable<{ success: boolean; message: string; data: { verified: boolean; expiresAt?: string }; error?: string }> {
    return this.http.post<{ success: boolean; message: string; data: { verified: boolean; expiresAt?: string }; error?: string }>(`${this.API_URL}/auth/verify-otp`, payload).pipe(
      catchError((error: any) => {
        // Handle HTTP errors (400, 404, etc.)
        const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Invalid or expired OTP';
        return throwError(() => ({ 
          success: false, 
          message: errorMessage,
          error: errorMessage
        }));
      }),
      map(response => {
        if (!response.success || response.error) {
          const errorMessage = response.error || response.message || 'Invalid or expired OTP';
          throw { 
            success: false, 
            message: errorMessage,
            error: errorMessage
          };
        }
        return response;
      })
    );
  }

  /**
   * Verify OTP and reset password (email or phone)
   */
  resetPasswordWithOtp(payload: { email?: string; phone?: string; otp: string; password: string; confirmPassword: string }): Observable<{ message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/auth/verify-otp-reset-password`, payload).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Password reset failed');
        }
        return { message: response.message };
      })
    );
  }

  /**
   * Reset password with token using backend API
   */
  resetPasswordWithToken(token: string, password: string, confirmPassword: string): Observable<{ message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/auth/reset-password`, {
      token,
      password,
      confirmPassword
    }).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Password reset failed');
        }
        return { message: response.message };
      })
    );
  }

  private setAuthData(user: User, token: string): void {
    // Store access token in memory only (not localStorage for security)
    // Refresh token is stored separately in localStorage (encrypted)
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
    
    this.currentUserSubject.next(user);
    this.tokenSubject.next(token);
  }

  /**
   * Load permissions from storage
   */
  private loadPermissions(): void {
    const permissions = this.getPermissions();
    if (permissions) {
      this.permissionsSubject.next(permissions);
    }
  }

  /**
   * Initialize auth state - called by APP_INITIALIZER
   */
  initializeAuth(): Promise<void> {
    return new Promise((resolve) => {
      if (!isPlatformBrowser(this.platformId)) {
        // During SSR, mark as initialized without checking localStorage
        this.authInitializedSubject.next(true);
        resolve();
        return;
      }

      // Load auth and wait for refresh to complete
      this.loadStoredAuth().then(() => {
        this.authInitializedSubject.next(true);
        resolve();
      });
    });
  }

  private loadStoredAuth(): Promise<void> {
    return new Promise((resolve) => {
      // Try to refresh token using stored refresh token to get new access token
      // Access token is not stored in localStorage (stored in memory only)
      const userStr = isPlatformBrowser(this.platformId) ? localStorage.getItem(this.USER_KEY) : null;
      const refreshToken = this.getRefreshToken();

      if (userStr && refreshToken) {
        try {
          const user = JSON.parse(userStr);
          this.currentUserSubject.next(user);
          // Load permissions if available
          this.loadPermissions();
          // Try to refresh token to get new access token
          // If refresh fails (no token or expired), clear auth data
          this.refreshToken().subscribe({
            next: (token) => {
              this.tokenSubject.next(token);
              resolve();
            },
            error: (error) => {
              // If refresh fails, clear auth data
              this.clearAuthData();
              resolve(); // Resolve anyway to not block app initialization
            }
          });
        } catch (error) {
          this.clearAuthData();
          resolve();
        }
      } else {
        // No stored user data or refresh token, clear everything
        this.clearAuthData();
        resolve();
      }
    });
  }

  /**
   * Store user permissions
   */
  private storePermissions(permissions: UserPermissions): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.PERMISSIONS_KEY, JSON.stringify(permissions));
    }
    this.permissionsSubject.next(permissions);
  }

  /**
   * Get user permissions
   */
  getPermissions(): UserPermissions | null {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(this.PERMISSIONS_KEY);
      if (stored) {
        try {
          const permissions = JSON.parse(stored);
          this.permissionsSubject.next(permissions);
          return permissions;
        } catch (e) {
        }
      }
    }
    return this.permissionsSubject.value;
  }

  /**
   * Check if user has permission for a module
   */
  hasPermission(module: string): boolean {
    const permissions = this.getPermissions();
    if (!permissions) {
      // If no permissions stored, user is main user - allow all
      return true;
    }
    
    const moduleMap: { [key: string]: keyof UserPermissions } = {
      'AI_AVATAR': 'avatar',
      'MEDIA': 'media',
      'DOCUMENTS': 'documents',
      'CHAT': 'chat',
      'MESSAGES': 'messages',
      'DIARY': 'diary',
      'TIME_CAPSULE': 'timeCapsule',
    };
    
    const permissionKey = moduleMap[module];
    return permissionKey ? permissions[permissionKey] : false;
  }

  private clearAuthData(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem(this.PERMISSIONS_KEY);
      this.removeRefreshToken(); // Clear encrypted refresh token
    }
    this.currentUserSubject.next(null);
    this.tokenSubject.next(null);
    this.permissionsSubject.next(null);
  }

  private generateDummyToken(user: User): string {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({
      sub: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours
    }));
    const signature = btoa('dummy-signature');
    
    return `${header}.${payload}.${signature}`;
  }

  private generateDummyRefreshToken(user: User): string {
    return btoa(`refresh-${user.id}-${Date.now()}`);
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }
}
